'use client';

import { useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { CreateClientSchema } from '@/schemas';
import { createClient } from '@/actions';
import { useTranslations } from '@/hooks/useTranslations';
import { useClientContext } from '@/contexts/client-context';
import { LoadingButton } from '@/components/loading-button';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { FormError } from '@/components/form';
import { toast } from 'sonner';

export const CreateClientForm = () => {
  const [error, setError] = useState<string | undefined>('');
  const { status, wrap } = useSubmitStatus();

  const t = useTranslations('CreateClientPopup');
  const common = useTranslations('Common');
  const clientsTable = useTranslations('ClientsTable');

  const form = useForm<z.infer<typeof CreateClientSchema>>({
    resolver: zodResolver(CreateClientSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      country: '',
      zipCode: '',
      taxNumber: '',
      registrationNumber: '',
    },
  });

  const { dispatch } = useClientContext();

  const onSubmit = (values: z.infer<typeof CreateClientSchema>) => {
    setError('');
    wrap(
      () =>
        new Promise<void>((resolve, reject) => {
          createClient(values)
            .then((data) => {
              if (data.success) {
                toast.success(data.success);
                resolve();

                setTimeout(() => {
                  dispatch({ type: 'CLOSE_CREATE_CLIENT_POPUP' });
                  form.reset();
                }, 1500);
              } else {
                const errorMessage = data.error || 'Something went wrong!';
                toast.error(errorMessage);
                setError(errorMessage);
                reject();
              }
            })
            .catch(() => {
              const errorMessage = 'Something went wrong!';
              toast.error(errorMessage);
              setError(errorMessage);
              reject();
            });
        })
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{clientsTable('tableHeaders.name')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('namePlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{clientsTable('tableHeaders.address')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('addressPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('zipCode')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('zipCodePlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('city')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('cityPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('country')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('countryPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="taxNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('taxNumber')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('taxNumberPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="registrationNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('regNumber')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('regNumberPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormError message={error} />

        <LoadingButton
          isFull={true}
          type="submit"
          status={status}
          idleText={common('confirm')}
        />
      </form>
    </Form>
  );
};
